import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { debounce, isEmpty } from 'underscore';
import classNames from 'classnames';

import connectOptions, { mergeProps } from '../../utils/connectOptions';
import * as browseActionCreators from '../../actions/browse';
import BrowseTable from '../../components/Browse/BrowseTable';
import { BrowseAutoCompleteTag } from '../../components/Browse/BrowseAutoCompleteTag';
import { BrowseDropDown } from '../../components/Browse/BrowseDropDown';
import BrowseInputTag from '../../components/Browse/BrowseInputTag';
import { BrowseProjectDefaultCriteriaFilter } from '../../components/Browse/BrowseProjectDefaultCriteriaFilter';
import { DirectorDashboardFilter } from '../../components/Browse/DirectorDashboardFilter';
import config from '../../config';
import {
  buildTagParam,
  buildSortByParam,
  buildDropdownParam,
  buildCheckboxObjectParam,
  buildRangeParam,
} from '../../helpers/paramBuilder';
import { showError } from '../../utils/MessagePopup';
import { checkBrowsePageAccess } from '../../utils/checkPermissions';
import { usePopupContext } from '../../contexts/PopupContext';
import { Card } from '../../components/helpers/Card';

import style from './style.module.css';

const projectsFilter = config.browse.getIn(['filter', 'projects']);
const columnDefs = config.browse.getIn(['table', 'columnDefs', 'projects']);

/**
 * Build all state data to restore it on a getting client.
 *
 * @param {object} projectsData Projects browse data.
 */
const buildFiltersStateParam = projectsData => {
  const stateParams = {};

  if (projectsData.getIn(['buyer', 'checked'])) stateParams.buyer = projectsData.get('buyer').toJS();
  if (projectsData.getIn(['harvcoContact', 'checked']))
    stateParams.harvcoContact = projectsData.get('harvcoContact').toJS();
  if (projectsData.getIn(['users', 'checked'])) stateParams.users = projectsData.get('users').toJS();
  if (projectsData.getIn(['active', 'checked'])) stateParams.active = projectsData.get('active').toJS();
  if (projectsData.getIn(['category', 'checked'])) stateParams.category = projectsData.get('category').toJS();
  if (projectsData.getIn(['industry', 'checked'])) stateParams.industry = projectsData.get('industry').toJS();
  if (projectsData.getIn(['keywords', 'checked'])) stateParams.keywords = projectsData.get('keywords').toJS();
  if (projectsData.getIn(['buyerProjectCriteria', 'checked']))
    stateParams.buyerProjectCriteria = projectsData.get('buyerProjectCriteria').toJS();
  if (projectsData.getIn(['transactionTypes', 'checked']))
    stateParams.transactionTypes = projectsData.get('transactionTypes').toJS();
  if (projectsData.getIn(['directorDashboard', 'checked']))
    stateParams.directorDashboard = projectsData.get('directorDashboard').toJS();

  return stateParams;
};

const buildRequestParam = projectsData => {
  const requestParams = {
    ...buildTagParam(projectsData, 'buyers', 'buyer', 'id'),
    ...buildTagParam(projectsData, 'harvco_leads', 'harvcoContact', 'id'),
    ...buildTagParam(projectsData, 'users', 'users', 'id'),
    ...buildDropdownParam(projectsData, 'active', 'active'),
    ...buildTagParam(projectsData, 'names', 'category'),
    ...buildTagParam(projectsData, 'industries', 'industry', 'value'),
    ...buildTagParam(projectsData, 'keywords', 'keywords', 'value'),
    ...buildCheckboxObjectParam(projectsData, 'transaction_types', 'transactionTypes'),
    ...buildRangeParam(projectsData.get('buyerProjectCriteria'), 'revenue_range', 'revenueRange'),
    ...buildRangeParam(projectsData.get('buyerProjectCriteria'), 'ebitda_range', 'ebitdaRange'),
    ...buildRangeParam(projectsData.get('buyerProjectCriteria'), 'ev_range', 'evRange'),
  };

  if (projectsData.getIn(['directorDashboard', 'checked'])) {
    const reportType = projectsData.getIn(['directorDashboard', 'reportType']);
    const data = projectsData.getIn(['directorDashboard', 'data']);

    requestParams.directorDashboard = {
      ...requestParams.directorDashboard,
      [reportType]: {
        ...data.toJS(),
      },
    };
  }

  return requestParams;
};

const BrowseProjectsContainer = props => {
  const {
    projects,
    user,
    location,
    handleFetchCriteria,
    setSearchParamsHash,
    handleBrowse,
    handleUpdateFilter,
    handleSearchParamsBeforeBrowse,
    handleGetBuyerSuggestion,
    handleGetHarvCoLeadsSuggestion,
    handleGetUsersSuggestion,
    handleGetCategorySuggestion,
    updateTabVisibility,
    handleClearFilter,
    handleAbortRequest,
    ...rest
  } = props;

  const { openPopup, closePopup } = usePopupContext();

  const projectsRef = useRef(projects);

  projectsRef.current = projects;

  const hasPageAccess = checkBrowsePageAccess(user, 'projects');

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const criteria = searchParams.get('criteria');

    if (criteria) {
      const payload = {
        name: 'projects',
        hash: criteria,
      };

      handleFetchCriteria(payload);
      setSearchParamsHash(payload);
    }

    if (!hasPageAccess) {
      showError(openPopup, config.permisionError, () => {
        closePopup();
      });
    }
  }, []);

  const sortModel = projects.getIn(['sortBy', 'sortModel']);
  const hasMountedRef = useRef(false);
  const previousSortModelRef = useRef(sortModel);

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      previousSortModelRef.current = sortModel;

      return;
    }

    if (sortModel !== previousSortModelRef.current) {
      const requestParam = buildRequestParam(projects);

      if (!isEmpty(requestParam)) {
        const sortBy = buildSortByParam(projects);

        handleBrowse({
          name: 'projects',
          params: requestParam,
          page: 1,
          sortBy,
        });
      }
    }

    previousSortModelRef.current = sortModel;
  }, [sortModel]);

  const handleChange = useCallback(
    data => {
      const extra = data.key ? { key: data.key } : {};

      handleUpdateFilter({
        ...extra,
        name: 'projects',
        filterName: data.filterName,
        filterData: data.filterData,
      });
    },
    [handleUpdateFilter],
  );

  const handleDefaultCriteriaChange = useCallback(
    data => {
      const extra = data.key ? { key: data.key } : {};

      handleUpdateFilter({
        ...extra,
        name: 'projects',
        filterName: 'buyerProjectCriteria',
        filterData: data.filterData,
      });
      handleUpdateFilter({
        ...extra,
        name: 'projects',
        filterName: 'transactionTypes',
        filterData: data.filterData,
      });

      handleUpdateFilter({
        ...extra,
        name: 'projects',
        filterName: 'buyerProjectCriteria',
        filterData: {
          revenueRange: data.filterData,
          ebitdaRange: data.filterData,
          evRange: data.filterData,
        },
      });
    },
    [handleUpdateFilter],
  );

  const handleTransactionTypeCheckboxChange = useCallback(
    data => {
      handleUpdateFilter({
        name: 'projects',
        filterName: 'transactionTypes',
        filterData: {
          [data.filterName]: data.filterData,
        },
      });
    },
    [handleUpdateFilter],
  );

  const handleDefaultCriteriaRangeChange = useCallback(
    data => {
      handleUpdateFilter({
        name: 'projects',
        filterName: 'buyerProjectCriteria',
        filterData: {
          [data.filterName]: data.filterData,
        },
      });
    },
    [handleUpdateFilter],
  );

  const handleGetNextPageData = useCallback(
    (page, keepSelection = true) => {
      const params = buildRequestParam(projects);
      const sortBy = buildSortByParam(projects);

      handleSearchParamsBeforeBrowse({
        params: {
          requestParams: params,
          stateParams: buildFiltersStateParam(projects),
        },
        name: 'projects',
        afterSuccess: () => {
          handleBrowse({
            name: 'projects',
            params,
            page,
            sortBy,
            keepSelection,
          });
        },
      });
    },
    [projects, handleSearchParamsBeforeBrowse, handleBrowse],
  );

  const fetchSuggestionsForBuyer = useCallback(
    ({ value }) => {
      handleGetBuyerSuggestion('projects', 'buyer', value);
    },
    [handleGetBuyerSuggestion],
  );

  const handleGetSuggestionForBuyer = useMemo(() => debounce(fetchSuggestionsForBuyer, 200), [
    fetchSuggestionsForBuyer,
  ]);

  const fetchNextSuggestionsForBuyer = useCallback(
    ({ value }) => {
      const info = projectsRef.current.getIn(['buyer', 'pagination']);
      const currentPage = info.get('currentPage', null);

      if (currentPage !== null && currentPage < info.get('totalPages')) {
        handleGetBuyerSuggestion('projects', 'buyer', value, currentPage + 1);
      }
    },
    [handleGetBuyerSuggestion],
  );

  const handleGetNextSuggestionForBuyer = useMemo(() => debounce(fetchNextSuggestionsForBuyer, 100), [
    fetchNextSuggestionsForBuyer,
  ]);

  const fetchSuggestionsForHarvcoLead = useCallback(
    ({ value }) => {
      handleGetHarvCoLeadsSuggestion('projects', value);
    },
    [handleGetHarvCoLeadsSuggestion],
  );

  const handleGetSuggestionForHarvcoLead = useMemo(() => debounce(fetchSuggestionsForHarvcoLead, 200), [
    fetchSuggestionsForHarvcoLead,
  ]);

  const fetchNextSuggestionsForHarvcoLead = useCallback(
    ({ value }) => {
      const info = projectsRef.current.getIn(['harvcoContact', 'pagination']);
      const currentPage = info.get('currentPage', null);

      if (currentPage !== null && currentPage < info.get('totalPages')) {
        handleGetHarvCoLeadsSuggestion('projects', value, currentPage + 1);
      }
    },
    [handleGetHarvCoLeadsSuggestion],
  );

  const handleGetNextSuggestionForHarvcoLead = useMemo(() => debounce(fetchNextSuggestionsForHarvcoLead, 100), [
    fetchNextSuggestionsForHarvcoLead,
  ]);

  const fetchSuggestionsForUsers = useCallback(
    ({ value }) => {
      handleGetUsersSuggestion('projects', 'users', value);
    },
    [handleGetUsersSuggestion],
  );

  const handleGetSuggestionForUsers = useMemo(() => debounce(fetchSuggestionsForUsers, 200), [
    fetchSuggestionsForUsers,
  ]);

  const fetchNextSuggestionsForUsers = useCallback(
    ({ value }) => {
      const info = projectsRef.current.getIn(['users', 'pagination']);
      const currentPage = info.get('currentPage', null);

      if (currentPage !== null && currentPage < info.get('totalPages')) {
        handleGetUsersSuggestion('projects', 'users', value, currentPage + 1);
      }
    },
    [handleGetUsersSuggestion],
  );

  const handleGetNextSuggestionForUsers = useMemo(() => debounce(fetchNextSuggestionsForUsers, 100), [
    fetchNextSuggestionsForUsers,
  ]);

  const fetchSuggestionsForCategory = useCallback(
    ({ value }) => {
      handleGetCategorySuggestion('projects', value);
    },
    [handleGetCategorySuggestion],
  );

  const handleGetSuggestionForCategory = useMemo(() => debounce(fetchSuggestionsForCategory, 200), [
    fetchSuggestionsForCategory,
  ]);

  const fetchNextSuggestionsForCategory = useCallback(
    ({ value }) => {
      const info = projectsRef.current.getIn(['category', 'pagination']);
      const currentPage = info.get('currentPage', null);

      if (currentPage !== null && currentPage < info.get('totalPages')) {
        handleGetCategorySuggestion('projects', value, currentPage + 1);
      }
    },
    [handleGetCategorySuggestion],
  );

  const handleGetNextSuggestionForCategory = useMemo(() => debounce(fetchNextSuggestionsForCategory, 100), [
    fetchNextSuggestionsForCategory,
  ]);

  const handleDoubleClickRow = useCallback(row => {
    window.open(`/project/${row.data.id}`, '_blank');
  }, []);

  const handleToggle = useCallback(() => {
    updateTabVisibility({
      field: 'projects.showTabControls',
      value: !projects.get('showTabControls'),
    });
  }, [projects, updateTabVisibility]);

  const handleFilterClear = useCallback(
    name => {
      handleClearFilter({ name });
    },
    [handleClearFilter],
  );

  const handleRequestAbort = useCallback(
    name => {
      handleAbortRequest(name);
    },
    [handleAbortRequest],
  );

  const controlClassName = classNames(style.controls, {
    [style.controlsHidden]: !projects.get('showTabControls'),
  });

  const params = {
    requestParams: buildRequestParam(projects),
    stateParams: buildFiltersStateParam(projects),
  };

  const hasParams = !isEmpty(params.requestParams);

  return (
    <div className="tab-pane active" id="browse-projects" role="tabpanel">
      {hasPageAccess && (
        <div className={style.inner}>
          <BrowseTable
            columnDefs={columnDefs}
            data={projects}
            hasParams={hasParams}
            location={location}
            name="projects"
            onAbortRequest={handleRequestAbort}
            onChange={handleChange}
            onClearFilter={handleFilterClear}
            onDoubleClickRow={handleDoubleClickRow}
            onGetNextPageData={handleGetNextPageData}
            onGetPageData={handleGetNextPageData}
            onSearchParamsBeforeBrowse={handleSearchParamsBeforeBrowse}
            onToggle={handleToggle}
            requestParams={params.requestParams}
            stateParams={params.stateParams}
            user={user}
            hideContextDropDown
            {...rest}
          />
          <Card className={controlClassName} title="Filters">
            <BrowseAutoCompleteTag
              filter={projectsFilter.get('buyer')}
              info={projects.get('buyer')}
              onChange={handleChange}
              onGetNextSuggestion={handleGetNextSuggestionForBuyer}
              onGetSuggestion={handleGetSuggestionForBuyer}
            />
            <BrowseAutoCompleteTag
              filter={projectsFilter.get('harvcoContact')}
              info={projects.get('harvcoContact')}
              onChange={handleChange}
              onGetNextSuggestion={handleGetNextSuggestionForHarvcoLead}
              onGetSuggestion={handleGetSuggestionForHarvcoLead}
            />
            <BrowseAutoCompleteTag
              filter={projectsFilter.get('users')}
              info={projects.get('users')}
              onChange={handleChange}
              onGetNextSuggestion={handleGetNextSuggestionForUsers}
              onGetSuggestion={handleGetSuggestionForUsers}
            />
            <BrowseDropDown
              filter={projectsFilter.get('active')}
              info={projects.get('active')}
              onChange={handleChange}
            />
            <BrowseAutoCompleteTag
              filter={projectsFilter.get('category')}
              info={projects.get('category')}
              onChange={handleChange}
              onGetNextSuggestion={handleGetNextSuggestionForCategory}
              onGetSuggestion={handleGetSuggestionForCategory}
            />
            <BrowseInputTag
              filter={projectsFilter.get('industry')}
              info={projects.get('industry')}
              onChange={handleChange}
              isBIC
              submitOnEnter
            />
            <BrowseInputTag
              filter={projectsFilter.get('keywords')}
              info={projects.get('keywords')}
              onChange={handleChange}
              shouldGroupTags
              submitOnEnter
            />
            <BrowseProjectDefaultCriteriaFilter
              filter={projectsFilter.get('buyerProjectCriteria')}
              info={projects.get('buyerProjectCriteria')}
              onChange={handleDefaultCriteriaChange}
              onChangeCheckbox={handleTransactionTypeCheckboxChange}
              onChangeRange={handleDefaultCriteriaRangeChange}
            />
            {projects.getIn(['directorDashboard', 'reportType']) && (
              <DirectorDashboardFilter info={projects.get('directorDashboard')} onChange={handleChange} />
            )}
          </Card>
        </div>
      )}
    </div>
  );
};

export { BrowseProjectsContainer };
export default connect(
  state => ({
    projects: state.browse.get('projects'),
    user: state.auth.get('user'),
  }),
  dispatch => bindActionCreators(browseActionCreators, dispatch),
  mergeProps,
  connectOptions,
)(memo(BrowseProjectsContainer));
